import {
  Dialog,
  Transition,
  DialogPanel,
  TransitionChild,
} from '@headlessui/react'
import {
  ArrowRightStartOnRectangleIcon,
  CheckCircleIcon,
  DocumentDuplicateIcon,
  // EyeIcon, // HIDDEN_PROFILES_REFERRALS: revert via PROFILES_AND_REFERRALS_HIDDEN.md
  XMarkIcon,
} from '@heroicons/react/20/solid'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { abbreviateAddress } from 'utils/formatting'
import { PublicKey } from '@solana/web3.js'
import { Wallet, useWallet } from '@solana/wallet-adapter-react'
import { IconButton, LinkButton } from '@components/shared/Button'
import { notify } from 'utils/notifications'
import WalletBalances from '@components/wallet/WalletBalances'
import { useCopyToClipboard } from 'hooks/useCopyToClipboard'
import { rouna, rounaDisplay, rounaMono } from 'utils/fonts'
import { useWalletTokens } from 'hooks/useWalletTokens'
import { useRouter } from 'next/router'
import WalletIcon from '@components/icons/WalletIcon'
import { useWalletTokensWithPrice } from 'hooks/useWalletTokensWithPrice'
import { useSolPrice } from 'hooks/useSolPrice'
import { formatCurrencyValue, formatNumericValue } from 'utils/numbers'
import ButtonGroup from '@components/forms/ButtonGroup'
import { useOrdersCount } from 'hooks/useOrdersCount'
import OrdersTabs from '@components/orders/OrdersTabs'
import { goToTokenPage } from 'utils/tokens'
import { TokenMetadataItem } from 'types/token'
import ProfileImage from '@components/profile/ProfileImage'
// import EditProfileModal from '@components/profile/EditProfileModal' // HIDDEN_PROFILES_REFERRALS: revert via PROFILES_AND_REFERRALS_HIDDEN.md
import { useProfileDetails } from '@components/profile/useProfileDetails'
import { useTranslation } from 'next-i18next'
import Tooltip from '@components/shared/Tooltip'

const WalletDrawer = ({
  publicKey,
  wallet,
  isOpen,
  onClose,
}: {
  publicKey: PublicKey
  wallet: Wallet
  isOpen: boolean
  onClose: () => void
}) => {
  const { t } = useTranslation('wallet')
  const router = useRouter()
  const { disconnect } = useWallet()
  const { refetch: fetchWalletTokens } = useWalletTokens()
  const { tokens } = useWalletTokensWithPrice()
  const { solPrice } = useSolPrice()
  const { isCopied, handleCopyAddress } = useCopyToClipboard()
  const [activeTab, setActiveTab] = useState<'balances' | 'limit-orders'>(
    'balances',
  )
  const { ordersCount } = useOrdersCount()
  const { profileDetails } = useProfileDetails()
  // HIDDEN_PROFILES_REFERRALS: revert via PROFILES_AND_REFERRALS_HIDDEN.md
  // const [showEditProfileModal, setShowEditProfileModal] = useState(false)

  const [walletValue, walletSolValue] = useMemo(() => {
    const value = tokens?.length
      ? tokens.reduce((acc, token) => acc + token.value, 0)
      : 0
    const solValue = solPrice ? value / solPrice : 0
    return [value, solValue]
  }, [tokens, solPrice])

  const handleDisconnect = useCallback(() => {
    void disconnect()
    notify({
      type: 'info',
      title: t('drawer.disconnected'),
    })
  }, [disconnect, t])

  useEffect(() => {
    void fetchWalletTokens()
  }, [fetchWalletTokens])

  const handleBalanceClick = useCallback(
    (mint: string, token?: TokenMetadataItem) => {
      goToTokenPage(mint, router, undefined, token)
      onClose()
    },
    [router, onClose],
  )

  // HIDDEN_PROFILES_REFERRALS: revert via PROFILES_AND_REFERRALS_HIDDEN.md
  // const handleEditProfileClick = useCallback(() => {
  //   setShowEditProfileModal(true)
  //   onClose()
  // }, [onClose])

  return (
    <>
      <Transition show={isOpen}>
        <Dialog className="fixed inset-0 z-30" onClose={onClose}>
          <TransitionChild
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            as={Fragment}
          >
            <div className="fixed inset-0 z-40 cursor-default bg-black/50" />
          </TransitionChild>
          <TransitionChild
            enter="transition ease-in duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-out duration-300"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
            as={Fragment}
          >
            <DialogPanel
              className={`thin-scroll absolute right-0 z-40 size-full overflow-y-auto bg-th-bkg-1 text-left font-body md:w-[480px] ${rouna.variable} ${rounaDisplay.variable} ${rounaMono.variable}`}
            >
              <div className="flex justify-end p-2">
                <button
                  onClick={onClose}
                  className="flex size-8 items-center justify-center rounded-full text-th-fgd-3 focus-visible:bg-th-bkg-3 md:hover:bg-th-bkg-2"
                >
                  <XMarkIcon className="size-5" />
                </button>
              </div>
              <div className="px-4 md:px-6">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center">
                    {profileDetails?.hasImage ? (
                      <ProfileImage imageSize="48" placeholderSize="32" />
                    ) : (
                      <div className="flex size-12 items-center justify-center rounded-full bg-th-bkg-3">
                        <WalletIcon className="size-6 text-th-fgd-3" />
                      </div>
                    )}
                    <div className="ml-3 overflow-hidden text-left">
                      <p className="mb-1.5 text-sm leading-none text-th-fgd-3">
                        {profileDetails?.username || wallet.adapter.name}
                      </p>
                      <p className="truncate pr-2 text-sm font-semibold leading-none text-th-fgd-1">
                        {abbreviateAddress(publicKey)}
                      </p>
                      {/* HIDDEN_PROFILES_REFERRALS: revert via PROFILES_AND_REFERRALS_HIDDEN.md
                      <LinkButton
                        onClick={handleEditProfileClick}
                        className="mt-1 text-xs"
                      >
                        <span className="text-th-button md:hover:text-th-button-hover">
                          {t('drawer.edit-profile')}
                        </span>
                      </LinkButton>
                      */}
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    {/* HIDDEN_PROFILES_REFERRALS: revert via PROFILES_AND_REFERRALS_HIDDEN.md
                    <Tooltip content={'View Account'}>
                      <IconButton
                        size="medium"
                        onClick={() =>
                          router.push(`/profile/${publicKey.toString()}`)
                        }
                      >
                        <EyeIcon className="size-4"></EyeIcon>
                      </IconButton>
                    </Tooltip>
                    */}
                    <Tooltip content={t('drawer.copy-address')}>
                      <IconButton
                        size="medium"
                        onClick={() => handleCopyAddress(publicKey.toString())}
                      >
                        {isCopied ? (
                          <CheckCircleIcon className="size-4 text-th-up" />
                        ) : (
                          <DocumentDuplicateIcon className="size-4" />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip content={t('drawer.disconnect')}>
                      <IconButton size="medium" onClick={handleDisconnect}>
                        <ArrowRightStartOnRectangleIcon className="size-4" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
                <div className="mb-8 flex flex-col items-center rounded-xl border border-th-bkg-3 px-4 py-2">
                  <p className="text-xl font-bold text-th-fgd-1">
                    {formatCurrencyValue(walletValue)}
                  </p>
                  <p>≈{formatNumericValue(walletSolValue)} SOL</p>
                </div>
                <ButtonGroup
                  activeValue={activeTab}
                  values={['balances', 'limit-orders']}
                  names={[
                    t('drawer.tabs.balances'),
                    `${t('drawer.tabs.limit-orders')} (${ordersCount})`,
                  ]}
                  onChange={setActiveTab}
                />
                <div className="py-4">
                  {activeTab === 'balances' ? (
                    <WalletBalances onClick={handleBalanceClick} />
                  ) : (
                    <OrdersTabs onClick={onClose} />
                  )}
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
      {/* HIDDEN_PROFILES_REFERRALS: revert via PROFILES_AND_REFERRALS_HIDDEN.md
      {showEditProfileModal ? (
        <EditProfileModal
          isOpen={showEditProfileModal}
          onClose={() => setShowEditProfileModal(false)}
        />
      ) : null}
      */}
    </>
  )
}

export default WalletDrawer
